if (!customElements.get("select-wrapper")) {
  customElements.define(
    "select-wrapper",
    class SelectWrapper extends HTMLElement {
      constructor() {
        super();

        intersecting({
          element: this,
          callback: this.init.bind(this),
          threshold: 200,
        });

        if (Shopify.designMode) {
          setTimeout(() => {
            this.init();
          }, 200);
        }

        this.select = this.querySelector("select");
        if (this.select) {
          this.select.addEventListener(
            "change",
            this.handleSelectChange.bind(this)
          );
        }
      }

      init() {
        const style = window.getComputedStyle(this.select);
        const value = this.select.options[this.select.selectedIndex].text;

        const text = document.createElement("span");
        text.style.fontFamily = style.fontFamily;
        text.style.fontSize = style.fontSize;
        text.style.fontWeight = style.fontWeight;
        text.style.visibility = "hidden";
        text.style.position = "absolute";
        text.innerHTML = value;

        document.body.appendChild(text);
        const width = text.clientWidth;

        this.style.setProperty("--width", `${width}px`);
        text.remove();
      }

      handleSelectChange() {
        this.init();
      }
    }
  );
}

if (!customElements.get("product-bundle")) {
  customElements.define(
    "product-bundle",
    class ProductBundle extends HTMLElement {
      constructor() {
        super();

        this.classes = {
          souldout: "f-price--sold-out",
          onsale: "f-price--on-sale",
          nocompare: "f-price--no-compare",
        };

        this.price = this.querySelector(".f-price");
        this.variants = this.querySelector("select");
        if (this.variants) {
          this.variants.addEventListener(
            "change",
            this.onSelectChange.bind(this)
          );
        }
      }

      onSelectChange(event) {
        const variants = event.target;
        const variant = variants.options[variants.selectedIndex];
        const compare_at_price = parseInt(
          variant.dataset.compare_at_price || 0
        );
        const price = parseInt(variant.dataset.price || 0);
        const available = variant.dataset.available === "true";
        const price_varies = variants.dataset.price_varies === "true";
        const compare_at_price_varies =
          variants.dataset.compare_at_price_varies === "true";

        // Remove classes
        this.price.classList.remove(this.classes.souldout);
        this.price.classList.remove(this.classes.onsale);
        this.price.classList.remove(this.classes.nocompare);

        // Add classes
        if (available === false) {
          this.price.classList.add(this.classes.souldout);
        } else if (compare_at_price > price && available) {
          this.price.classList.add(this.classes.onsale);
        }
        if (price_varies == false && compare_at_price_varies) {
          this.price.classList.add(this.classes.nocompare);
        }

        // Change price
        const price__regular = this.querySelector(".f-price__regular");
        price__regular.querySelector(".f-price-item--regular").innerHTML =
          formatMoney(price, window.FoxThemeSettings.money_format);

        const price__sale = this.querySelector(".f-price__sale");
        price__sale.querySelector(".f-price-item--regular").innerHTML =
          formatMoney(compare_at_price, window.FoxThemeSettings.money_format);
        price__sale.querySelector(".f-price-item--sale").innerHTML =
          formatMoney(price, window.FoxThemeSettings.money_format);
      }
    }
  );
}

if (!customElements.get("products-bundle")) {
  customElements.define(
    "products-bundle",
    class ProductsBundle extends HTMLElement {
      constructor() {
        super();
        this.selectedProducts = {};
        this.init();
      }

      init() {
        this.setupAccordion();
        this.setupProductSelection();
        this.setupCTA();
      }

      // ── Accordion ──────────────────────────────────────────────
      setupAccordion() {
        this.querySelectorAll(".products-bundle__step-header").forEach(
          (header) => {
            header.addEventListener("click", (e) => {
              e.preventDefault();
              const step = header.closest(".products-bundle__step");
              const content = step.querySelector(
                ".products-bundle__step-content"
              );
              const isOpen = step.classList.contains("is-open");
              step.classList.toggle("is-open", !isOpen);
              content.classList.toggle("is-open", !isOpen);
              header.setAttribute("aria-expanded", String(!isOpen));
            });
          }
        );
      }

      // ── Product radio selection ────────────────────────────────
      setupProductSelection() {
        this.querySelectorAll(".products-bundle__radio-input").forEach(
          (input) => {
            // Capture checked state BEFORE the click processes
            input.addEventListener("mousedown", () => {
              input.dataset.wasChecked = input.checked ? "true" : "false";
            });

            input.addEventListener("click", () => {
              if (input.dataset.wasChecked === "true") {
                // Was already selected — deselect it
                input.checked = false;
                input.dataset.wasChecked = "false";
                const stepIndex = input.dataset.step;

                // Hide variant select for this step
                this.querySelectorAll(
                  `.products-bundle__radio-input[data-step="${stepIndex}"]`
                ).forEach((other) => {
                  const item = other.closest(".products-bundle__product-wrap");
                  const sel = item && item.querySelector(".products-bundle__variant-select");
                  if (sel) sel.style.display = "none";
                });

                delete this.selectedProducts[stepIndex];

                // Clear step header label
                const stepEl = this.querySelector(
                  `.products-bundle__step[data-step="${stepIndex}"]`
                );
                if (stepEl) {
                  const nameEl = stepEl.querySelector(".products-bundle__step-selected-name");
                  if (nameEl) nameEl.textContent = "";
                }

                this.updateCTA();
              }
            });

            input.addEventListener("change", () => {
              if (!input.checked) return;
              const stepIndex = input.dataset.step;

              // Reset wasChecked on siblings
              this.querySelectorAll(
                `.products-bundle__radio-input[data-step="${stepIndex}"]`
              ).forEach((other) => { other.dataset.wasChecked = "false"; });
              input.dataset.wasChecked = "true";

              // Show variant select only for chosen product in this step
              this.querySelectorAll(
                `.products-bundle__radio-input[data-step="${stepIndex}"]`
              ).forEach((other) => {
                const item = other.closest(".products-bundle__product-wrap");
                const sel = item && item.querySelector(".products-bundle__variant-select");
                if (sel) sel.style.display = other === input ? "" : "none";
              });

              const item = input.closest(".products-bundle__product-wrap");
              const variantDropdown = item && item.querySelector(".products-bundle__variant-dropdown");

              this.selectedProducts[stepIndex] = {
                input,
                variantDropdown,
                get variantId() {
                  return variantDropdown ? variantDropdown.value : input.value;
                },
                // Price getter — reads the CURRENTLY selected dropdown variant's price.
                // Using a static value would give the wrong price if the customer picks
                // a non-default variant from the select.
                get price() {
                  if (variantDropdown && variantDropdown.options[variantDropdown.selectedIndex]) {
                    const optPrice = variantDropdown.options[variantDropdown.selectedIndex].dataset.price;
                    if (optPrice) return parseInt(optPrice, 10);
                  }
                  return parseInt(input.dataset.productPrice, 10);
                },
                title: input.dataset.productTitle,
                productId: parseInt(input.dataset.productId, 10),
              };

              // Update step header selected-name label
              const stepEl = this.querySelector(
                `.products-bundle__step[data-step="${stepIndex}"]`
              );
              if (stepEl) {
                const nameEl = stepEl.querySelector(".products-bundle__step-selected-name");
                if (nameEl) nameEl.textContent = "\u2014 " + input.dataset.productTitle;
              }

              this.updateCTA();
            });
          }
        );
      }

      // ── Discount calculation ───────────────────────────────────
      getDiscountValue() {
        const count = Object.keys(this.selectedProducts).length;
        // Tiers are fixed server-side in Supabase env vars — mirror them here for display only.
        if (count >= 4) return 20;
        if (count >= 2) return 15;
        return 0;
      }

      // Kept for backward compat
      getDiscountPercent() {
        return this.getDiscountValue();
      }

      // Format cents using the theme's own formatMoney — handles all local currencies
      formatMoneyCents(cents) {
        return formatMoney(cents, window.FoxThemeSettings.money_format);
      }

      // Total cart value of selected products in cents
      getTotalSelectedCents() {
        return Object.values(this.selectedProducts).reduce(
          (sum, p) => sum + (p.price || 0), 0
        );
      }

      // Monetary saving based on selected products and active discount %
      getSavingsCents() {
        const pct = this.getDiscountValue();
        if (pct <= 0) return 0;
        return Math.round(this.getTotalSelectedCents() * pct / 100);
      }

      // Total to pay after discount applied
      getTotalAfterDiscountCents() {
        return this.getTotalSelectedCents() - this.getSavingsCents();
      }

      updateCTA() {
        const discountPct = this.getDiscountValue();
        const count = Object.keys(this.selectedProducts).length;
        const d2 = 15; // matches server-side BUNDLE_DISCOUNT_2 env var
        const btn = this.querySelector(".products-bundle__button-atc");
        if (!btn) return;
        const textEl = btn.querySelector(".product-card__atc-text");
        const badge = this.querySelector(".products-bundle__discount-badge");

        if (discountPct > 0) {
          const total = this.getTotalAfterDiscountCents();
          const totalLabel = total > 0
            ? this.formatMoneyCents(total)
            : `${discountPct}% OFF`;
          textEl.textContent = `ADD TO CART \u2014 ${totalLabel}`;
          if (badge) {
            const savings = this.getSavingsCents();
            const savingsStr = savings > 0 ? ` (${this.formatMoneyCents(savings)} saved)` : "";
            badge.textContent = `${discountPct}% off applied${savingsStr} \u2014 ${count} steps selected`;
          }
        } else {
          textEl.textContent = btn.dataset.defaultText || "Add to cart";
          if (badge) {
            badge.textContent =
              count === 1
                ? `Select 1 more product from a different step to unlock ${d2}% off`
                : "Select products from each step to unlock a discount";
          }
        }
      }

      // ── CTA setup & add-to-cart ────────────────────────────────
      setupCTA() {
        const btn = this.querySelector(".products-bundle__button-atc");
        if (btn) btn.addEventListener("click", this.onButtonClick.bind(this));
        this.updateCTA();
      }

      onButtonClick(event) {
        event.preventDefault();

        const discount    = this.getDiscountValue();
        const functionUrl = this.dataset.functionUrl;

        if (!functionUrl || discount <= 0) return;

        const selected = Object.values(this.selectedProducts);
        if (selected.length < 2) return;

        const variantIds = selected.map((p) => parseInt(p.variantId, 10));
        const productIds = selected.map((p) => p.productId);
        const titles     = selected.map((p) => p.title);

        // Country code is required so the function looks up the correct market priceList
        // (e.g. MYR for MY customers). Pricing is resolved entirely server-side via
        // MarketCatalog.priceList — no client-side currency conversion needed.
        const countryCode = (window.Shopify && window.Shopify.country) || "XX";

        const btn = this.querySelector(".products-bundle__button-atc") || this.querySelector("button");
        btn.setAttribute("disabled", "true");
        btn.classList.add("btn--loading");

        // ── Step 1: Get or create the Shopify bundle product ──────
        fetch(functionUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            variant_ids:    variantIds,
            product_ids:    productIds,
            variant_titles: titles,
            discount_value: discount,
            country_code:   countryCode,
          }),
        })
          .then((r) => {
            if (!r.ok) return r.json().then((e) => { throw new Error(e.error || r.statusText); });
            return r.json();
          })
          .then((data) => {
            if (!data.bundle_variant_id) throw new Error("No bundle_variant_id in response");

            // ── Step 2: Collect sections for cart drawer render ──
            let sectionToBundle = [];
            document.documentElement.dispatchEvent(
              new CustomEvent("cart:grouped-sections", {
                bubbles: true,
                detail: { sections: sectionToBundle },
              })
            );
            const cartDrawer = document.querySelector("cart-drawer");
            if (cartDrawer && typeof cartDrawer.getSectionsToRender === "function") {
              sectionToBundle = [
                ...sectionToBundle,
                ...cartDrawer.getSectionsToRender().map((s) => s.id),
              ];
            }
            const mainCart = document.querySelector("cart-items");
            if (mainCart && typeof mainCart.getSectionsToRender === "function") {
              sectionToBundle = [
                ...sectionToBundle,
                ...mainCart.getSectionsToRender().map((s) => s.section),
              ];
            }

            // ── Step 3: Add the single bundle variant to cart ────
            return fetch(FoxThemeSettings.routes.cart_add_url, {
              method: "POST",
              headers: { "Content-Type": "application/json", Accept: "application/javascript" },
              body: JSON.stringify({
                items: [{ id: data.bundle_variant_id, quantity: 1 }],
                sections: sectionToBundle,
                sections_url: window.location.pathname,
              }),
            }).then((r) => r.json());
          })
          .then((cartResponse) => {
            if (cartResponse?.status) {
              const errorEl = this.querySelector(".product-form__error-message");
              if (errorEl) errorEl.textContent = cartResponse.description;
              return;
            }
            // Open cart drawer — emit cartUpdate, never dispatch cart:refresh
            window.FoxThemeEvents.emit(PUB_SUB_EVENTS.cartUpdate, cartResponse);
          })
          .catch((e) => {
            console.error("[ProductsBundle]", e);
            const errorEl = this.querySelector(".product-form__error-message");
            if (errorEl) errorEl.textContent = e.message || "Something went wrong. Please try again.";
          })
          .finally(() => {
            btn.classList.remove("btn--loading");
            btn.removeAttribute("disabled");
          });
      }

      // Patch every checkout link/button on the page to include the discount code
      _applyDiscountToCheckout(code) {
        if (!code) return;

        // FoxTheme's cart drawer uses <form id="CartDrawer" action="/cart">
        // with a checkout submit button — intercept it and redirect with discount
        const cartForm = document.getElementById("CartDrawer");
        if (cartForm && !cartForm._bundleDiscountPatched) {
          cartForm._bundleDiscountPatched = true;
          cartForm.addEventListener("submit", (e) => {
            const submitter = e.submitter;
            if (submitter && submitter.name === "checkout") {
              e.preventDefault();
              window.location.href = `/checkout?discount=${encodeURIComponent(code)}`;
            }
          });
        }

        // Also patch [data-checkout-button] buttons directly
        document.querySelectorAll("[data-checkout-button]").forEach((btn) => {
          if (!btn._bundleDiscountPatched) {
            btn._bundleDiscountPatched = true;
            btn.addEventListener("click", (e) => {
              e.preventDefault();
              e.stopPropagation();
              window.location.href = `/checkout?discount=${encodeURIComponent(code)}`;
            }, true);
          }
        });

        // Patch any direct /checkout anchor links
        document.querySelectorAll('a[href*="/checkout"]').forEach((a) => {
          try {
            const url = new URL(a.href, window.location.origin);
            url.searchParams.set("discount", code);
            a.href = url.toString();
          } catch (_) {}
        });

        // Store for any late-rendered drawers
        window._bundleDiscountCode = code;
        sessionStorage.setItem("bundle_discount_code", code);
      }

      // ── Legacy hover handlers kept for lookbook hotspot ints ──
      onEnterHandler() {}
      onLeaveHandler() {}

      // ── Stub — kept for compatibility with existing theme code ─
      get classes() {
        return { hover: "is-hover", active: "is-active" };
      }
    }
  );
}
